import { useState, useRef, useEffect, type RefObject } from "react";
import { Search, ChevronDown } from "lucide-react";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface SearchablePicklistProps {
    id?: string;
    label?: string;
    required?: boolean;
    options: string[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    searchPlaceholder?: string;
    className?: string;
    error?: string | null;
    invalid?: boolean;
    buttonRef?: RefObject<HTMLButtonElement>;
    "data-testid"?: string;
}

export function SearchablePicklist({
    id,
    label,
    required,
    options,
    value,
    onChange,
    placeholder = "Select an option",
    searchPlaceholder = "Search...",
    className,
    error,
    invalid,
    buttonRef,
    "data-testid": testId,
}: SearchablePicklistProps) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const filtered = options.filter((o) =>
        o.toLowerCase().includes(query.toLowerCase())
    );

    return (
        <div className="relative space-y-2" ref={ref}>
            {label && <Label htmlFor={id}>{label}{required && <span className="text-red-500 ml-0.5">*</span>}</Label>}
            <button
                id={id}
                type="button"
                ref={buttonRef}
                onClick={() => { setOpen((o) => !o); setQuery(""); }}
                className={cn(
                    "flex items-center justify-between w-full h-12 rounded-xl border border-slate-800 bg-white px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                    (error || invalid) && "border-red-500",
                    className
                )}
                aria-invalid={!!(error || invalid)}
                aria-describedby={error?.trim() && id ? `${id}-error` : undefined}
                data-testid={testId}
            >
                <span className={value ? "text-slate-900" : "text-slate-400"}>
                    {value || placeholder}
                </span>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {error && error.trim() && <p id={id ? `${id}-error` : undefined} className="text-xs text-red-500">{error}</p>}
            {open && (
                <div className="absolute z-50 mt-1 w-full rounded-xl border border-slate-700 bg-white shadow-xl overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-500">
                        <Search className="w-4 h-4 text-slate-400 shrink-0" />
                        <input
                            autoFocus
                            placeholder={searchPlaceholder}
                            className="flex-1 text-sm bg-transparent focus:outline-none placeholder:text-slate-400"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                    </div>
                    <ul className="max-h-48 overflow-y-auto">
                        {filtered.length === 0 && (
                            <li className="px-4 py-3 text-sm text-slate-400">No results</li>
                        )}
                        {filtered.map((o) => (
                            <li
                                key={o}
                                className={`px-4 py-2.5 text-sm cursor-pointer hover:bg-slate-50 ${
                                    value === o ? "bg-slate-100 font-semibold" : ""
                                }`}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                    onChange(o);
                                    setOpen(false);
                                }}
                            >
                                {o}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
